import { Button } from '@lmig/lmds-react-button';
import { Form } from '@lmig/lmds-react-form';
import { Link } from '@lmig/lmds-react-link';
import { Select, SelectOption } from '@lmig/lmds-react-select';
import { Table, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { TextField } from '@mui/material';
import { useState, FunctionComponent, useEffect } from 'react';
import { HttpStatusCodes } from 'Utilities/ClientResponseUtility';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import './SalesCompUI.scss';
import { useUserData } from 'Foundation/User/UserContext';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';

const SalesCompUI: FunctionComponent = () => {
  const [salesCompResults, setSalesCompResults] = useState<{
    filteredItems: any[];
    hideDropDown: boolean;
    isLoaded: boolean;
    items: any[];
  }>({
    filteredItems: [],
    hideDropDown: true,
    isLoaded: false,
    items: [],
  });
  const { userData } = useUserData();
  const [searchTerm, setSearchTerm] = useState<string>(userData?.selectedStatCodeData?.SubStat ?? '');
  const [agencySearchInputErrored, setAgencySearchInputErrored] = useState<boolean>(false);
  const [docTypeFilter, setDocTypeFilter] = useState<string>('ALL');
  const [dateFilter, setDateFilter] = useState<string>(new Date()?.getFullYear()?.toString() ?? '');
  const [agencyName, setAgencyName] = useState<string>('');
  const [noAccessMessage, setNoAccessMessage] = useState<string>('');
  const [responseStatus, setResponseStatus] = useState<{ statusCode: number; message: string }>({
    statusCode: HttpStatusCodes.InternalServerError,
    message: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const spellCheck = (misspelled: string) => {
    return misspelled?.replace('Commision', 'Commission');
  };

  const pastYears = () => {
    let current = new Date().getFullYear();
    let years = new Array<number>();
    while (current !== 2016) {
      years.push(current - 1);
      current -= 1;
    }
    return years;
  };

  const filterDates = (data: any[]) => {
    var year = dateFilter;
    if (dateFilter === 'current') {
      year = new Date().getFullYear().toString();
    }
    return data?.filter((x) => new Date(x.documentDate).getFullYear().toString() === year);
  };

  const filterDocTypes = (data: any[]) => {
    if (docTypeFilter !== 'ALL') {
      return data?.filter((x) => getFileExtension(x) === docTypeFilter.toLowerCase());
    } else {
      return data;
    }
  };

  const getDateString = (date: string) => {
    let str = '';
    try {
      let d = new Date(date);
      var m = ('0' + (d.getMonth() + 1)).slice(-2);
      var dy = ('0' + d.getDate()).slice(-2);
      var y = d.getFullYear();
      str = m + '-' + dy + '-' + y;
    } catch (error) {}
    return str;
  };

  const getMimeType = (mimeType: string): string => {
    let mime = '';
    try {
      mime = mimeType.split('/')[1]?.toUpperCase();
    } catch (error) {}
    return mime;
  };

  const validateStatCode = () => {
    if (searchTerm) {
      let search = formatStatCode(searchTerm);
      if (search.match(/^\d{8}$/)) {
        setAgencySearchInputErrored(false);
        return;
      }
    }
    setAgencySearchInputErrored(true);
  };

  const agencySalesCompSearch = async () => {
    setIsLoading(true);
    try {
      if (!agencySearchInputErrored) {
        let search = formatStatCode(searchTerm);
        await getAgencyNameForStatCode(search);
        await getSalesCompDocsForStatcode(search);
        setIsLoading(false);
        return;
      }
    } catch (error) {}
    setIsLoading(false);
  };

  const formatStatCode = (statcode: string) => {
    let search = statcode.trim();
    return search.replaceAll('-', '');
  };

  async function getSalesCompDocsForStatcode(statCode: string) {
    let result: any = [];
    try {
      let path = `/api/sitecore/documents/searchcompstatments?statcode=${statCode}`;
      let response = await SitecoreFetch(path);
      if (response.Status.Code === HttpStatusCodes.OK) result = response.Data;
      else result = [];
      setResponseStatus({ statusCode: response.Status.Code, message: response.Status.Message });
    } catch (error) {
      console.error(error);
    }
    setNoAccessMessage(
      `${
        userData?.userType?.toLowerCase() === 'agent' || userData?.isMockingAgent
          ? `The current user, ${userData?.dpecid},`
          : `This employee user`
      } does not have access to ${searchTerm}.`,
    );
    setSalesCompResults((prevState) => ({
      ...prevState,
      isLoaded: true,
      items: result,
      filteredItems: [],
    }));
  }

  async function getAgencyNameForStatCode(statCode: string) {
    try {
      let path = `/api/sitecore/AgencyAPIAsync/GetAgencyByStatcode?statcode=${statCode}`;
      let options = { method: 'POST' };
      let response = await SitecoreFetch(path, options);
      if (response.Status.Code === HttpStatusCodes.OK) {
        if (response.Data && response.Data.Name) {
          setAgencyName(response.Data.Name);
          return;
        }
      }
    } catch (error) {
      console.error(error);
    }
    setAgencyName('');
    return;
  }

  const getFileExtension = (item: any): string => {
    if (item.fileName) return item.fileName.split('.')[item.fileName.split('.').length - 1].toLowerCase();
    else return getMimeType(item.mimeType).toLowerCase();
  };

  useEffect(() => {
    let docs = salesCompResults?.items;
    if (docs && docs.length > 0) {
      docs = filterDates(docs);
      docs = filterDocTypes(docs);
      setSalesCompResults((prevState) => ({ ...prevState, filteredItems: docs }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, docTypeFilter, salesCompResults?.items]);

  useEffect(() => {
    if (searchTerm !== '') agencySalesCompSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div id="search-parameters">
        <Form>
          <TextField
            id="agency-search-input"
            label="8-digit agency number"
            variant="outlined"
            className="agency-search offset-text-field"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target?.value)}
            onBlur={() => validateStatCode()}
            error={agencySearchInputErrored}
            helperText={agencySearchInputErrored ? 'Please enter the full 8-digit agency number' : ''}
            color={!agencySearchInputErrored ? 'primary' : 'secondary'}
          />
          <Button
            id="agency-comp-search"
            className="agency-comp-search"
            data-testid="agency-comp-search"
            variant="primary"
            onClick={() => agencySalesCompSearch()}
            type="submit"
          >
            Search
          </Button>
        </Form>
        <Form>
          <Select
            id="time-period-select"
            className="time-period-select"
            isRequired
            labelA11y="Time period"
            labelVisual="Time period"
            onChange={(e) => setDateFilter(e.currentTarget.value)}
            defaultValue="current"
          >
            <SelectOption value="current">Current year</SelectOption>
            {pastYears().map((year) => (
              <SelectOption value={year.toString()}>{year}</SelectOption>
            ))}
          </Select>
          <Select
            id="doc-type-select"
            className="doc-type-select"
            isRequired
            labelA11y="Document type"
            labelVisual="Type"
            onChange={(e) => setDocTypeFilter(e.currentTarget.value)}
            defaultValue="ALL"
          >
            <SelectOption value="ALL">All</SelectOption>
            <SelectOption value="PDF">PDF</SelectOption>
            <SelectOption value="CSV">CSV</SelectOption>
          </Select>
        </Form>
      </div>
      <div id="sales-comp-results-table">
        {isLoading ? (
          <CircularProgressSpinner id="salesCompSpinner" size="32" aria-label="Loading sales comp data..." />
        ) : !agencySearchInputErrored && salesCompResults && salesCompResults.filteredItems?.length > 0 ? (
          <Table id="policy-lookup-policy-table" className="uscm-recent-activities-table">
            <TableHeader>
              <TableRow>
                <TableCell type="colHead">Type</TableCell>
                <TableCell type="colHead">Document name</TableCell>
                <TableCell type="colHead">Agency</TableCell>
                <TableCell type="colHead">Agency code</TableCell>
                <TableCell type="colHead">Date created</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {salesCompResults.filteredItems.map((item, i) => (
                <TableRow key={i}>
                  <TableCell>{getFileExtension(item).toUpperCase()}</TableCell>
                  <TableCell>
                    <Link
                      href={`/docs/comp/${item.agencyCode}/${item.id}.${getFileExtension(item).toLowerCase()}`}
                      target="_blank"
                      className="document-link"
                    >
                      {item.documentName ?? spellCheck(item.documentType)}
                    </Link>
                  </TableCell>
                  <TableCell>{agencyName}</TableCell>
                  <TableCell>{item.agencyCode}</TableCell>
                  <TableCell>{getDateString(item.documentDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : responseStatus.statusCode === HttpStatusCodes.OK ? (
          <div className="no-results-message">
            <p>No Agency Compensation documents found for this time period.</p>
          </div>
        ) : (
          salesCompResults.isLoaded && (
            <div className="no-results-message">
              <p>{noAccessMessage}</p>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default SalesCompUI;
